import React from 'react';
import './OverviewCard.scss';

// Card chrome
interface OverviewCardProps {
  title?: React.ReactNode;
  headerRight?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}

export const OverviewCard = ({ title, headerRight, className, bodyClassName, children }: OverviewCardProps) => (
  <div className={['overview-card', className].filter(Boolean).join(' ')}>
    {(title || headerRight) && (
      <div className='overview-card__header'>
        {title && <div className='overview-card__title'>{title}</div>}
        {headerRight && <div className='overview-card__header-right'>{headerRight}</div>}
      </div>
    )}
    <div className={['overview-card__body', bodyClassName].filter(Boolean).join(' ')}>{children}</div>
  </div>
);

// Field (label + value pair). Works in both stacked and inline layouts.
interface OverviewFieldProps {
  label?: React.ReactNode;
  value: React.ReactNode;
  layout?: 'stacked' | 'inline';
  labelWidth?: number | string;
  valueClassName?: string;
  className?: string;
}

export const OverviewField = ({
  label,
  value,
  layout = 'stacked',
  labelWidth,
  valueClassName,
  className,
}: OverviewFieldProps) => (
  <div className={['overview-field', `overview-field--${layout}`, className].filter(Boolean).join(' ')}>
    {label && (
      <span
        className='overview-field__label'
        style={layout === 'inline' && labelWidth ? { width: labelWidth, flex: `0 0 ${typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth}` } : undefined}
      >
        {label}
      </span>
    )}
    <span className={['overview-field__value', valueClassName].filter(Boolean).join(' ')}>{value ?? '-'}</span>
  </div>
);

// Inline utility: icon + text on the same row (for value slots)
interface OverviewInlineProps {
  children: React.ReactNode;
  gap?: number;
  className?: string;
}

export const OverviewInline = ({ children, gap = 6, className }: OverviewInlineProps) => (
  <span className={['overview-inline', className].filter(Boolean).join(' ')} style={{ gap }}>
    {children}
  </span>
);

// Grid layout: uniform rows × columns of stacked fields (DeviceStage style)
export interface GridField {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}

interface OverviewGridProps {
  /** Maximum number of columns. Rows with fewer fields stretch to fill the row. */
  columns?: number;
  rows: GridField[][];
}

export const OverviewGrid = ({ columns = 4, rows }: OverviewGridProps) => (
  <div className='overview-grid'>
    {rows.map((row, rowIndex) => {
      const cols = Math.min(row.length, columns);
      return (
        <div
          className='overview-grid__row'
          key={rowIndex}
          style={{ ['--overview-cols' as any]: cols }}
        >
          {row.map((field, cellIndex) => (
            <div className='overview-grid__cell' key={cellIndex}>
              <OverviewField
                layout='stacked'
                label={field.label}
                value={field.value}
                valueClassName={field.valueClassName}
              />
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

// Sections layout: flex-wrap of titled sections with inline fields
// (ReaderStage style)
interface OverviewSectionsProps {
  children: React.ReactNode;
  className?: string;
}

export const OverviewSections = ({ children, className }: OverviewSectionsProps) => (
  <div className={['overview-sections', className].filter(Boolean).join(' ')}>{children}</div>
);

export interface SectionField {
  label: string;
  value: React.ReactNode;
}

interface OverviewSectionProps {
  title: string;
  fields?: SectionField[];
  labelWidth?: number | string;
  minWidth?: string;
  children?: React.ReactNode;
  className?: string;
}

export const OverviewSection = ({
  title,
  fields,
  labelWidth = 110,
  minWidth = '220px',
  children,
  className,
}: OverviewSectionProps) => (
  <div className={['overview-section', className].filter(Boolean).join(' ')} style={{ minWidth }}>
    <div className='overview-section__title'>{title}</div>
    <div className='overview-section__fields'>
      {fields?.map((f, i) => (
        <OverviewField key={i} layout='inline' labelWidth={labelWidth} label={f.label} value={f.value} />
      ))}
      {children}
    </div>
  </div>
);